import { Flex, Text } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { MangoButtonVariant } from '~/components/shared/componentStyles/MangoButton.style'
import { DateInput } from '~/components/shared/DateInput'
import { MangoButton } from '~/components/shared/MangoButton'
import { MangoModal } from '~/components/shared/MangoModal'
import { TimeInput } from '~/components/shared/TimeInput'
import { useKeyPresses } from '~/hooks/useKeyboard'
import { textStyleKeys } from '~/themes/textStyles'
import { DateRange, TimePeriodRange } from '~/types/Shared'

import {
  TimePeriod,
  tryGetMergedTimePeriodDate,
  tryGetTimePeriod,
} from '@mangovoice/core'
import { TimePeriodRange as CoreTimePeriodRange } from '@mangovoice/core'

enum PeriodName {
  After = 'after',
  Before = 'before',
}

interface DateRangeModalProps {
  isOpen?: boolean
  dateTimeRange?: DateRange
  onClose?: () => void
  onClear?: () => void
  onSet?: (dateTimeRange: DateRange) => void
}

export const DateTimeRangeModal = ({
  isOpen,
  dateTimeRange: externalDateTimeRange,
  onClose,
  onClear,
  onSet,
}: DateRangeModalProps) => {
  const [dateRange, setDateRange] = useState<DateRange>(
    externalDateTimeRange ?? DateRange.empty(),
  )

  const [timeRange, setTimeRange] = useState<CoreTimePeriodRange>(
    TimePeriodRange.empty,
  )

  const setTimeRangeValue = (
    periodName: PeriodName,
    timePeriod?: TimePeriod,
  ) => {
    const newTimeRange: CoreTimePeriodRange = { ...timeRange }
    newTimeRange[periodName] = timePeriod

    setTimeRange(newTimeRange)
  }

  const setDateRangeValue = (period: PeriodName, value: Date | undefined) => {
    const newDateRange: DateRange = { ...dateRange }
    newDateRange[period] = value

    setDateRange(newDateRange)
  }

  const handleOnSet = () => {
    const newAfterDate = tryGetMergedTimePeriodDate(
      dateRange.after,
      timeRange.after,
    )

    const newBeforeDate = tryGetMergedTimePeriodDate(
      dateRange.before,
      timeRange.before,
    )

    onSet?.({
      after: newAfterDate,
      before: newBeforeDate,
    })

    // close modal
    onClose?.()
  }

  useEffect(() => {
    if (externalDateTimeRange) {
      const timePeriodRange = {
        before: tryGetTimePeriod(externalDateTimeRange.before),
        after: tryGetTimePeriod(externalDateTimeRange.after),
      }

      setTimeRange(timePeriodRange)
      setDateRange({ ...externalDateTimeRange })
    }
  }, [
    externalDateTimeRange,
    externalDateTimeRange?.before,
    externalDateTimeRange?.after,
  ])

  useKeyPresses({
    onPress: (e: KeyboardEvent) => isOpen && e.key === 'Enter' && handleOnSet(),
  })

  const afterDate = dateRange?.after
  const afterTime = timeRange?.after

  const beforeDate = dateRange?.before
  const beforeTime = timeRange?.before

  return (
    <MangoModal
      isOpen={isOpen}
      onClose={onClose}
      header="Set Date Range"
      body={
        <Flex direction="column">
          <Flex
            data-testid="date-time-selection-header"
            alignSelf="stretch"
            alignItems="center"
            gap="1.25rem"
          >
            <Text textStyle={textStyleKeys.Body2_Bold}>
              After{' '}
              <Text as="span" textStyle={textStyleKeys.Body2}>
                (optional)
              </Text>
            </Text>
          </Flex>
          <Flex
            data-testid="date-time-selection-wrapper"
            alignSelf="stretch"
            gap="1.25rem"
            py="0.5rem"
            alignItems="center"
          >
            <DateInput
              value={afterDate}
              mountsFocused
              onDateSet={(val) => setDateRangeValue(PeriodName.After, val)}
            />
            <TimeInput
              timePeriod={afterTime}
              disabled={!afterDate}
              onTimeSet={(timePeriod) =>
                setTimeRangeValue(PeriodName.After, timePeriod)
              }
            />
          </Flex>
          <Flex
            data-testid="date-time-selection-header"
            alignSelf="stretch"
            alignItems="center"
            gap="1.25rem"
          >
            <Text textStyle={textStyleKeys.Body2_Bold}>
              Before{' '}
              <Text as="span" textStyle={textStyleKeys.Body2}>
                (optional)
              </Text>
            </Text>
          </Flex>
          <Flex
            data-testid="date-time-selection-wrapper"
            alignSelf="stretch"
            gap="1.25rem"
            py="0.5rem"
            alignItems="center"
          >
            <DateInput
              value={beforeDate}
              onDateSet={(val) => setDateRangeValue(PeriodName.Before, val)}
            />
            <TimeInput
              timePeriod={beforeTime}
              disabled={!beforeDate}
              onTimeSet={(timePeriod) =>
                setTimeRangeValue(PeriodName.Before, timePeriod)
              }
            />
          </Flex>
        </Flex>
      }
      footer={
        <Flex gap="0.75rem">
          <MangoButton
            variant={MangoButtonVariant.Default_100}
            onClick={onClear}
          >
            Clear
          </MangoButton>
          <MangoButton
            type="submit"
            variant={MangoButtonVariant.Default_300}
            onClick={handleOnSet}
          >
            Set Date Range
          </MangoButton>
        </Flex>
      }
    />
  )
}
